/**
 *
 * VideoConferences
 *
 */

import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Alert, Button, Modal, Card, ProgressBar } from 'react-bootstrap';
import { VideoHost, VideoConfContext, VIDEO_CONF_NONE } from 'components/VideoConf/index.js';
import PrimaryAction from 'components/interaction/PrimaryAction/index.jsx';
import PopupLink from 'components/PopupLink/index.jsx';
import { intlTags } from 'utils/intlFunctions.jsx';
import sleep from 'utils/sleep.js';
import useAlert from 'containers/Alerts/useAlert.js';
import messages from './messages.js';
import { LOCATION_TYPE_VIDEO } from '../SelectLocationType/constants.js';
import { NumbersSpan } from './NumbersSpan.js';
import { PRIVACY_VALUES } from './constants.jsx';

function VideoConferences(props) {
  const { onChange, meetingList } = props;

  const [show, setShow] = useState(false);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState(0);
  const { addError } = useAlert();
  const { provider, profile, addVideo } = useContext(VideoConfContext);
  const videoMeetings = meetingList.filter((o) => o.details.assigned && o.meetingLocationType === LOCATION_TYPE_VIDEO);
  const videoMeetingCount = videoMeetings.length;
  const hasVideoLink = (mt) => mt.videoMeeting && mt.videoMeeting.link;
  const videoLinkCount = videoMeetings.filter(hasVideoLink).length;
  const handleHide = () => setShow(false);

  async function handleSubmit() {
    try {
      const missingMeetings = videoMeetings.filter((o) => !hasVideoLink(o));
      setWorking(true);
      const iMax = missingMeetings.length;
      for (let i = 0; i < iMax; i++) {
        setProgress(100 * (i / iMax));
        const meeting = missingMeetings[i];
        // eslint-disable-next-line no-await-in-loop
        const videoMeeting = await addVideo(meeting);
        onChange({ ...meeting, videoMeeting });
        sleep(1000);
      }
    } catch (error) {
      addError(error);
    } finally {
      setProgress(0);

      setWorking(false);
      handleHide();
    }
  }

  function renderText() {
    const privacyPopup = <PopupLink id="privacy-link" title={<FormattedMessage {...messages.privacyStatement} />} />;
    const smallPrivacyLink = (
      <div className="small">
        <FormattedMessage {...messages.privacyStatementLink} values={PRIVACY_VALUES} />
        {privacyPopup}
      </div>
    );

    if (provider === VIDEO_CONF_NONE) {
      return (
        <div>
          <FormattedMessage {...messages.privacyVideo} values={PRIVACY_VALUES} />
          {privacyPopup}
        </div>
      );
    }
    if (videoMeetingCount === 0) {
      return (
        <>
          <Alert variant="info"><FormattedMessage {...messages.noVideoMeetingsAssigned} /></Alert>
          {smallPrivacyLink}
        </>
      );
    }
    return (
      <div>
        <div className="mb-3">
          <NumbersSpan>
            {videoLinkCount}
            /
            {videoMeetingCount}
          </NumbersSpan>
          <FormattedMessage {...messages.videoMeetingsLinked} />
        </div>
        {videoMeetingCount > videoLinkCount && (
          <div>
            <Button variant="light" className="mb-3" onClick={() => setShow(true)}>
              <FormattedMessage {...messages.addVideoLinks} />
            </Button>
            <Modal show={show} onHide={handleHide}>
              <Modal.Header closeButton>
                <Modal.Title>
                  <FormattedMessage {...messages.addVideoLinks} />
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="narrow">
                <FormattedMessage {...messages.videoModal} values={{ ...profile, ...intlTags(['ul', 'li', 'b', 'p']) }} />
                {working && <ProgressBar now={progress} />}
              </Modal.Body>
              <Modal.Footer>
                <PrimaryAction onCancel={handleHide} onSubmit={handleSubmit} disabled={working} submitText={messages.addLinks} />
              </Modal.Footer>
            </Modal>
          </div>
        )}
        {smallPrivacyLink}
      </div>
    );
  }
  return (
    <Card className="border p-4">
      <h4 className="mt-0"><FormattedMessage {...messages.videoConferences} /></h4>
      <div>
        <div className="mb-4 flex-grow-1">
          <div className="fw-semibold small mb-2"><FormattedMessage {...messages.provider} /></div>
          <VideoHost />
        </div>
        {renderText()}
      </div>
    </Card>
  );
}

VideoConferences.propTypes = {
  onChange: PropTypes.func,
  meetingList: PropTypes.array,
};

export default VideoConferences;
